import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getProductReviews,
  getUserReviews,
  hasUserReviewedProduct,
  createReview,
  updateReview,
  deleteReview,
  getPendingReviews,
  approveReview,
  rejectReview,
  getProductReviewStats,
} from '@/data/userReviews';
import { UserReviewInsert, UserReviewUpdate } from '@/contracts/userReviews';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

/**
 * React Query hooks for user reviews
 * Aggressive caching (5min staleTime) since reviews change infrequently
 */

// Get approved reviews for a product
export function useProductReviews(productId: string) {
  return useQuery({
    queryKey: ['product-reviews', productId],
    queryFn: () => getProductReviews(productId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

// Get user's own reviews
export function useUserReviews(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-reviews', userId],
    queryFn: () => {
      if (!userId) throw new Error('User ID required');
      return getUserReviews(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

// Check if user has reviewed a product
export function useHasUserReviewed(userId: string | undefined, productId: string) {
  return useQuery({
    queryKey: ['has-reviewed', userId, productId],
    queryFn: () => {
      if (!userId) return false;
      return hasUserReviewedProduct(userId, productId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

// Get product review stats
export function useProductReviewStats(productId: string) {
  return useQuery({
    queryKey: ['review-stats', productId],
    queryFn: () => getProductReviewStats(productId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

// Create review mutation
export function useCreateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createReview,
    onSuccess: (data) => {
      toast.success('Review submitted! It will appear after admin approval.');
      queryClient.invalidateQueries({ queryKey: ['product-reviews', data.product_id] });
      queryClient.invalidateQueries({ queryKey: ['user-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['has-reviewed'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats', data.product_id] });
    },
    onError: (error: any) => {
      if (error.code === '23505') {
        toast.error('You have already reviewed this product');
      } else {
        toast.error('Failed to submit review. Please try again.');
      }
    },
  });
}

// Update review mutation
export function useUpdateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, updates }: { reviewId: string; updates: UserReviewUpdate }) =>
      updateReview(reviewId, updates),
    onSuccess: (data) => {
      toast.success('Review updated successfully');
      queryClient.invalidateQueries({ queryKey: ['product-reviews', data.product_id] });
      queryClient.invalidateQueries({ queryKey: ['user-reviews'] });
    },
    onError: () => {
      toast.error('Failed to update review');
    },
  });
}

// Delete review mutation
export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteReview,
    onSuccess: () => {
      toast.success('Review deleted');
      queryClient.invalidateQueries({ queryKey: ['product-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['user-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['has-reviewed'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
    },
    onError: () => {
      toast.error('Failed to delete review');
    },
  });
}

// Admin: Get pending reviews
export function usePendingReviews() {
  const { isAdmin } = useAuth();

  return useQuery({
    queryKey: ['pending-reviews'],
    queryFn: getPendingReviews,
    enabled: isAdmin,
    staleTime: 1000 * 60 * 2, // 2 minutes (more frequent for moderation)
    gcTime: 1000 * 60 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

// Admin: Approve review
export function useApproveReview() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (reviewId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      return approveReview(reviewId, user.id);
    },
    onSuccess: (data) => {
      toast.success('Review approved');
      queryClient.invalidateQueries({ queryKey: ['pending-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['product-reviews', data.product_id] });
      queryClient.invalidateQueries({ queryKey: ['review-stats', data.product_id] });
    },
    onError: () => {
      toast.error('Failed to approve review');
    },
  });
}

// Admin: Reject review
export function useRejectReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectReview,
    onSuccess: () => {
      toast.success('Review rejected and deleted');
      queryClient.invalidateQueries({ queryKey: ['pending-reviews'] });
    },
    onError: () => {
      toast.error('Failed to reject review');
    },
  });
}
